import { useEffect, useRef, useState } from "react";
import { Eraser, PenTool } from "lucide-react";
import { useApp } from "../context/AppContext";

export default function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(!value);
  const { theme } = useApp();

  // Setup canvas; re-init when theme changes so stroke color matches
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.strokeStyle = theme === "dark" ? "#f97316" : "#1f2937";

    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = value;
      setEmpty(false);
    }
  }, [theme]); // eslint-disable-line react-hooks/exhaustive-deps

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setEmpty(false);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setEmpty(true);
    onChange?.(null);
  };

  return (
    <div className="space-y-2" data-testid="signature-pad">
      <div className="flex items-center justify-between">
        <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">
          Firma del Ingeniero
        </span>
        <button
          type="button"
          onClick={clear}
          className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 hover:text-orange-500 transition-colors flex items-center gap-1"
          data-testid="signature-clear-btn"
        >
          <Eraser className="w-3 h-3" />
          Borrar
        </button>
      </div>
      <div className="relative w-full h-48 bg-zinc-50 dark:bg-zinc-900 border border-dashed border-zinc-300 dark:border-zinc-700 rounded-md overflow-hidden corner-brackets">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full touch-none cursor-crosshair"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid="signature-canvas"
        />
        {empty && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none gap-1">
            <PenTool className="w-5 h-5 text-zinc-400 dark:text-zinc-600" />
            <p className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-400 dark:text-zinc-600">
              Firme aquí
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
